class ContactForm {
  private formElement: HTMLFormElement;
  private inputWrappers: HTMLElement[] = [];
  private textareas: HTMLTextAreaElement[] = [];
  private submitButton: HTMLButtonElement | null = null;
  private formContainer: HTMLElement | null = null;

  constructor(formEl: HTMLElement) {
    this.formElement = formEl as HTMLFormElement;
    this.inputWrappers = Array.from(
      this.formElement.querySelectorAll<HTMLElement>(".floating-label-wrapper")
    );
    this.textareas = Array.from(
      this.formElement.querySelectorAll<HTMLTextAreaElement>("textarea")
    );
    this.submitButton = this.formElement.querySelector<HTMLButtonElement>("button[type='submit']");
    this.formContainer = this.formElement.closest<HTMLElement>(".formContainer");

    // Bind methods
    this.handleInputFocus = this.handleInputFocus.bind(this);
    this.handleInputBlur = this.handleInputBlur.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.autoResizeTextarea = this.autoResizeTextarea.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  public init() {
    if (!this.formElement) return;

    // Setup form submission
    this.formElement.addEventListener("submit", this.handleSubmit);

    // Setup floating labels
    this.inputWrappers.forEach((wrapper) => {
      const input = wrapper.querySelector<HTMLInputElement | HTMLTextAreaElement>("input, textarea");
      if (!input) return;

      // Check if input has value on load (for form repopulation)
      if (input.value) {
        wrapper.classList.add("has-value");
      }

      input.addEventListener("focus", () => this.handleInputFocus(wrapper));
      input.addEventListener("blur", () => this.handleInputBlur(wrapper, input));
      input.addEventListener("input", () => this.handleInputChange(wrapper, input));

      // Listen for invalid event to show red border
      input.addEventListener("invalid", () => {
        input.style.borderBottomColor = "#DC2626";
        input.setAttribute("data-validation-error", "true");
      });
    });

    // Setup auto-resize textareas
    this.textareas.forEach((textarea) => {
      textarea.addEventListener("input", () => this.autoResizeTextarea(textarea));
      // Initialize height on load
      this.autoResizeTextarea(textarea);
    });
  }

  private async handleSubmit(e: Event) {
    e.preventDefault();

    // Clear any existing error messages
    const existingError = this.formElement.querySelector(".form-error-message:not([data-error-template])");
    if (existingError) {
      existingError.remove();
    }

    // Highlight invalid fields BEFORE reportValidity so they appear with the popup
    const isValid = this.formElement.checkValidity();
    if (!isValid) {
      this.highlightInvalidFields();
      this.formElement.reportValidity();
      return;
    }

    // Disable submit button during submission
    if (this.submitButton) {
      this.submitButton.disabled = true;
    }

    try {
      const formData = new FormData(this.formElement);
      const action = this.formElement.action;

      const response = await fetch(action, {
        method: "POST",
        body: formData,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "Accept": "application/json",
        },
      });

      if (response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const data = await response.json();
          this.showSuccessMessage(data.heading, data.message);
        } else {
          // Server returned HTML instead of JSON - likely still succeeded
          this.showSuccessMessage();
        }
      } else {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const data = await response.json();
          this.showErrorMessage(data.message || "Something went wrong. Please try again.");
        } else {
          this.showErrorMessage("Something went wrong. Please try again.");
        }
      }
    } catch (error) {
      console.error("Form submission error:", error);
      this.showErrorMessage("Network error. Please check your connection and try again.");
    } finally {
      // Re-enable submit button (only if there was an error - success replaces the form)
      if (this.submitButton && this.formContainer?.querySelector(".form-error-message")) {
        this.submitButton.disabled = false;
      }
    }
  }

  private showSuccessMessage(heading?: string, message?: string) {
    if (!this.formContainer) return;

    // Get the success message template
    const successTemplate = document.getElementById("contact-form-success-message");
    if (!successTemplate) {
      console.error("Success message template not found");
      return;
    }

    // Clone the template and make it visible
    const successMessage = successTemplate.cloneNode(true) as HTMLElement;
    successMessage.classList.remove("hidden");
    successMessage.removeAttribute("id");

    // Update heading if provided by server
    if (heading) {
      const headingElement = successMessage.querySelector("h3");
      if (headingElement) {
        headingElement.textContent = heading;
      }
    }

    // Update message text if provided by server
    if (message) {
      const textElement = successMessage.querySelector("p");
      if (textElement) {
        textElement.textContent = message;
      }
    }

    // Replace form container content with success message
    this.formContainer.innerHTML = "";
    this.formContainer.appendChild(successMessage);
  }

  private showErrorMessage(message?: string) {
    // Get the error message template
    const errorTemplate = document.getElementById("contact-form-error-message");
    if (!errorTemplate) {
      console.error("Error message template not found");
      return;
    }

    // Remove any existing error message
    const existingError = this.formElement.querySelector(".form-error-message:not([data-error-template])");
    if (existingError) {
      existingError.remove();
    }

    // Clone the template and make it visible
    const errorMessage = errorTemplate.cloneNode(true) as HTMLElement;
    errorMessage.classList.remove("hidden");
    errorMessage.removeAttribute("id");
    errorMessage.removeAttribute("data-error-template");

    // Optionally update the message text if provided
    if (message) {
      const textElement = errorMessage.querySelector("p");
      if (textElement) {
        textElement.textContent = message;
      }
    }

    // Insert error message at the top of the form
    this.formElement.insertBefore(errorMessage, this.formElement.firstChild);
  }

  private handleInputFocus(wrapper: HTMLElement) {
    wrapper.classList.add("focused");
  }

  private handleInputBlur(wrapper: HTMLElement, input: HTMLInputElement | HTMLTextAreaElement) {
    wrapper.classList.remove("focused");
    if (input.value) {
      wrapper.classList.add("has-value");
    } else {
      wrapper.classList.remove("has-value");
    }
  }

  private handleInputChange(wrapper: HTMLElement, input: HTMLInputElement | HTMLTextAreaElement) {
    if (input.value) {
      wrapper.classList.add("has-value");
    } else {
      wrapper.classList.remove("has-value");
    }

    // Remove invalid state when user types if field becomes valid
    if (input.validity.valid && input.hasAttribute("data-validation-error")) {
      input.style.borderBottomColor = "";
      input.removeAttribute("data-validation-error");
    }
  }

  private highlightInvalidFields() {
    // Find all invalid inputs and add red border
    const inputs = this.formElement.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("input:invalid, textarea:invalid");
    inputs.forEach((input) => {
      // Add red border with !important to override default styles
      input.style.borderBottomColor = "#DC2626"; // Tailwind red-600 (signal-vulcano equivalent)
      input.setAttribute("data-validation-error", "true");
    });
  }

  private autoResizeTextarea(textarea: HTMLTextAreaElement) {
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = "auto";
    // Set height to scrollHeight to fit content
    textarea.style.height = textarea.scrollHeight + "px";
  }

  public destroy() {
    this.formElement.removeEventListener("submit", this.handleSubmit);

    this.inputWrappers.forEach((wrapper) => {
      const input = wrapper.querySelector<HTMLInputElement | HTMLTextAreaElement>("input, textarea");
      if (!input) return;

      input.removeEventListener("focus", () => this.handleInputFocus(wrapper));
      input.removeEventListener("blur", () => this.handleInputBlur(wrapper, input));
      input.removeEventListener("input", () => this.handleInputChange(wrapper, input));
    });

    this.textareas.forEach((textarea) => {
      textarea.removeEventListener("input", () => this.autoResizeTextarea(textarea));
    });
  }
}

// Auto-init
function initContactForm() {
  const forms = Array.from(
    document.querySelectorAll<HTMLElement>(".contact-form")
  );
  if (forms.length === 0) return;

  const instances: ContactForm[] = [];
  for (const el of forms) {
    const instance = new ContactForm(el);
    instance.init();
    instances.push(instance);
  }
  (window as any)._contactFormInstances = instances;
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initContactForm);
} else {
  initContactForm();
}
